import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { QmaConstant } from '../constant/qma-constant';
import { AuthModeService } from '../auth/auth-mode.service';
import { environment } from '../../environments/environment';

/**
 * Attaches the appropriate auth token to outgoing HTTP requests:
 * - In IDP mode, uses the OAuth2/OIDC access token from OAuthService.
 * - In COT mode, uses the Symphony JWT stored in sessionStorage.
 *
 * Do not add Authorization to requests to the OAuth issuer (e.g. Okta token/discovery/JWKS),
 * or the IdP will reject the request (e.g. 401 on token endpoint).
 */
@Injectable()
export class AuthenticationInterceptor implements HttpInterceptor {
  constructor(
    private readonly oauthService: OAuthService,
    private readonly authModeService: AuthModeService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (this.isRequestToOAuthIssuer(request)) {
      return next.handle(request);
    }

    if (this.isIdpMode()) {
      const accessToken = this.getIdpAccessToken();
      if (!accessToken) {
        return next.handle(request);
      }
      const authRequest = request.clone({
        setHeaders: this.buildAuthHeaders(accessToken),
      });
      return next.handle(authRequest);
    }

    // Default: Symphony Circle-of-Trust JWT flow (backward compatible).
    const jwt = this.getCotJwt();
    if (!jwt) {
      return next.handle(request);
    }
    const authRequest = request.clone({
      setHeaders: this.buildAuthHeaders(jwt),
    });

    return next.handle(authRequest);
  }

  private isRequestToOAuthIssuer(request: HttpRequest<any>): boolean {
    const issuer = environment.ISSUER?.trim();
    if (!issuer) return false;
    const url = request.url?.trim() ?? '';
    return url.startsWith(issuer);
  }

  private isIdpMode(): boolean {
    return this.authModeService.isIdp();
  }

  private getIdpAccessToken(): string | null {
    return this.oauthService.getAccessToken() || null;
  }

  private getCotJwt(): string | null {
    return sessionStorage.getItem('symphony_jwt');
  }

  private buildAuthHeaders(token: string): { [header: string]: string } {
    return {
      originContext: QmaConstant.BASE_URL.substring(1),
      Authorization: `Bearer ${token}`,
    };
  }
}

